<template>
    <b-modal
        id="edit-row-modal"
        :visible="isVisible"
        :title="modalTitle"
        size="xl"
        @hide="handleHide"
        @shown="onModalShown"
        hide-footer
        hide-header
        body-class="p-0"
        content-class="carbon-frameless-modal-content"
    >
        <div class="edit-row-container">
            <b-form @submit.prevent="saveChanges">
                <div class="split-root">
                    <!-- Sidebar: Config Selection -->
                    <div class="split-sidebar">
                        <div class="sidebar-head">
                            <div class="sidebar-title">배출원 목록</div>
                            <div class="sidebar-sub">검색 후 배출원을 선택하세요</div>
                            <div class="search-wrap">
                                <i class="fas fa-search search-icon"></i>
                                <input
                                    v-model="configSearch"
                                    class="search-input"
                                    type="text"
                                    placeholder="시설/카테고리 검색"
                                />
                            </div>
                            <div class="sidebar-meta">
                                <span class="badge-soft">ACTIVE</span>
                                <span class="meta-text" v-if="loadingConfigs">불러오는 중…</span>
                                <span class="meta-text" v-else>{{ filteredConfigs.length }}건</span>
                            </div>
                        </div>

                        <div class="sidebar-list-wrap" v-if="filteredConfigs.length > 0">
                            <div class="sidebar-list-blur sidebar-list-blur-top" v-show="!isSidebarScrolledToTop"></div>
                            <div class="sidebar-list" ref="sidebarList" @scroll="onSidebarScroll">
                                <button
                                    v-for="cfg in filteredConfigs"
                                    :key="cfg.configIdx"
                                    type="button"
                                    class="cfg-item"
                                    :class="{ active: cfg.configIdx === editData.configIdx, disabled: !isCreateMode }"
                                    :disabled="!isCreateMode"
                                    @click="selectConfig(cfg)"
                                >
                                    <div class="cfg-top">
                                        <div class="cfg-facility">
                                            {{ cfg.facility?.facilityName || "조직 전체" }}
                                        </div>
                                        <div class="cfg-top-right">
                                            <span v-if="getScopeLabel(cfg)" class="scope-pill">{{
                                                getScopeLabel(cfg)
                                            }}</span>
                                        </div>
                                    </div>
                                    <div class="cfg-name">
                                        {{ cfg.category?.categoryName || `카테고리 #${cfg.categoryIdx}` }}
                                    </div>
                                    <div class="cfg-sub">
                                        <span class="cfg-chip" v-if="cfg.preferredFuel">{{ cfg.preferredFuel }}</span>
                                        <span class="cfg-chip" v-if="cfg.preferredUnit || cfg.defaultUnit">
                                            {{ cfg.preferredUnit || cfg.defaultUnit }}
                                        </span>
                                    </div>
                                </button>
                            </div>
                            <div
                                class="sidebar-list-blur sidebar-list-blur-bottom"
                                v-show="!isSidebarScrolledToEnd"
                            ></div>
                        </div>
                        <div class="sidebar-empty" v-else>
                            <div class="empty-title">조회된 배출원이 없습니다</div>
                            <div class="empty-desc">조직/기간에 맞는 배출원이 없거나, 검색 조건에 결과가 없습니다.</div>
                        </div>
                    </div>

                    <!-- Detail & Entry Pane -->
                    <div class="split-detail">
                        <div class="detail-body">
                            <div class="detail-head">
                                <div class="detail-title">{{ isCreateMode ? "활동자료 입력" : "활동자료 편집" }}</div>
                            </div>

                            <div class="detail-card">
                                <div class="summary-row">
                                    <div class="summary-k">배출원</div>
                                    <div class="summary-v" v-if="selectedConfig">
                                        <div class="summary-main">
                                            {{ selectedConfig.category?.categoryName || "-" }}
                                        </div>
                                        <div class="summary-sub">
                                            <strong>{{ selectedConfig.facility?.facilityName || "조직 전체" }}</strong>
                                        </div>
                                    </div>
                                    <div class="summary-v muted" v-else>좌측에서 배출원을 선택하세요</div>
                                </div>

                                <div v-if="selectedConfig" class="config-summary-bar">
                                    <div class="cs-item">
                                        <div class="cs-k">조직</div>
                                        <div class="cs-v">{{ selectedConfig.organization?.orgName || "-" }}</div>
                                    </div>
                                    <div class="cs-item">
                                        <div class="cs-k">연료</div>
                                        <div class="cs-v">{{ selectedConfig.preferredFuel || "-" }}</div>
                                    </div>
                                    <div class="cs-item">
                                        <div class="cs-k">단위</div>
                                        <div class="cs-v">
                                            {{ selectedConfig.preferredUnit || selectedConfig.defaultUnit || "-" }}
                                        </div>
                                    </div>
                                    <div class="cs-item">
                                        <div class="cs-k">Tier</div>
                                        <div class="cs-v">{{ selectedConfig.tier || "-" }}</div>
                                    </div>
                                </div>

                                <div class="form-fields">
                                    <div class="form-fields__row form-fields__row--top">
                                        <div class="form-block form-block--period">
                                            <label class="form-label">기간</label>
                                            <div class="period-wrap">
                                                <b-form-input
                                                    v-if="fixedPeriod"
                                                    :value="fixedPeriodFormatted"
                                                    readonly
                                                    disabled
                                                    class="form-input"
                                                />
                                                <DatePicker
                                                    v-else
                                                    v-model="periodFormatted"
                                                    type="month"
                                                    format="YYYY-MM"
                                                    value-type="format"
                                                    placeholder="연월 선택"
                                                    class="period-picker form-input"
                                                    :clearable="false"
                                                />
                                            </div>
                                            <b-form-invalid-feedback :state="validatePeriod">
                                                기간을 선택해주세요
                                            </b-form-invalid-feedback>
                                        </div>

                                        <div class="form-block form-block--amount">
                                            <label class="form-label">활동량</label>
                                            <div class="input-with-suffix">
                                                <b-form-input
                                                    v-model.number="editData.amount"
                                                    type="number"
                                                    step="any"
                                                    min="0"
                                                    :state="validateAmount"
                                                    class="form-input"
                                                    placeholder="0"
                                                    required
                                                />
                                                <div class="suffix">
                                                    {{
                                                        selectedConfig?.preferredUnit ||
                                                        selectedConfig?.defaultUnit ||
                                                        "-"
                                                    }}
                                                </div>
                                            </div>
                                            <b-form-invalid-feedback :state="validateAmount">
                                                활동량은 0 이상이어야 합니다
                                            </b-form-invalid-feedback>
                                        </div>
                                    </div>

                                    <div class="form-fields__row form-fields__row--bottom">
                                        <div class="form-block form-block--status">
                                            <label class="form-label">상태</label>
                                            <div class="segmented segmented--compact">
                                                <button
                                                    v-for="opt in draftStatSegmentOptions"
                                                    :key="opt.value"
                                                    type="button"
                                                    class="seg-btn"
                                                    :class="{ active: editData.draftStat === opt.value }"
                                                    @click="editData.draftStat = opt.value"
                                                >
                                                    {{ opt.text }}
                                                </button>
                                            </div>
                                        </div>

                                        <div class="form-block form-block--datasource">
                                            <label class="form-label">데이터 출처</label>
                                            <CarbonSelect
                                                customClass="entry-row-select"
                                                :value="editData.dataSource == null ? '' : editData.dataSource"
                                                :options="dataSourceOptions"
                                                placeholder="선택"
                                                :placeholderDisabled="false"
                                                @input="(v) => (editData.dataSource = v === '' ? null : v)"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="detail-footer">
                            <b-button variant="secondary" @click="handleHide" :disabled="saving">취소</b-button>
                            <b-button variant="success" type="submit" :disabled="!isValid || saving || !hasChanges">
                                <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                                {{ saving ? "저장 중..." : "저장" }}
                            </b-button>
                        </div>
                    </div>
                </div>
            </b-form>
        </div>
    </b-modal>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import CarbonCodes from "../../../codes";
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonSelect from "../../common/CarbonSelect.vue";

export default {
    name: "EditRowModal",
    components: {
        DatePicker,
        CarbonSelect,
    },
    props: {
        mode: {
            type: String,
            default: "edit", // 'create', 'edit', 'local' - 기본값으로 하위 호환성 유지
            validator: (v) => ["create", "edit", "local"].includes(v),
        },
        visible: {
            type: Boolean,
            default: false,
        },
        rowData: {
            type: Object,
            default: () => ({}),
        },
        // 하위 호환: 과거에 시설/카테고리를 직접 입력받던 모달 인터페이스
        facilities: { type: Array, default: () => [] },
        categories: { type: Array, default: () => [] },
        facilityMap: { type: Object, default: () => ({}) },
        categoryMap: { type: Object, default: () => ({}) },
        loginUser: {
            type: Object,
            default: null, // create 모드에서만 필수
        },
        /** 스텝 컨텍스트: 기간 고정 (YYYYMM) - 있으면 기간 필드 비활성화 */
        fixedPeriod: {
            type: [String, Number],
            default: null,
        },
        /** 스텝 컨텍스트: 조직 고정 (orgIdx) - activity-configs 조회 필터 */
        fixedOrgIdx: {
            type: [String, Number],
            default: null,
        },
        /** 저장 핸들러 (Promise 반환) - 성공 시 부모가 모달 닫기, 실패 시 throw */
        onSave: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            isVisible: false,
            editData: {
                activityIdx: null,
                userIdx: null,
                period: "",
                amount: 0,
                configIdx: null,
                draftStat: "DRAFT",
                /** POST/PUT activity-data body.dataSource — 공통코드 DATA_SOURCE (서버: max 255) */
                dataSource: null,
            },
            originalData: {},
            saving: false,
            configs: [],
            loadingConfigs: false,
            configSearch: "",
            categoryDetailByCode: {},
            isSidebarScrolledToEnd: false,
            isSidebarScrolledToTop: true,
        };
    },
    computed: {
        /** 배출원 설정과 동일 — 공통코드 DATA_SOURCE */
        dataSourceOptions() {
            return CarbonCodes.dataSource.getList();
        },
        draftStatOptions() {
            const list = CarbonCodes.draftStat.getList();
            const allowed = ["DRAFT", "ACTIVE"];
            const filtered = list.filter((c) => allowed.includes(c.value));
            return filtered.length > 0
                ? filtered
                : [
                      { value: "DRAFT", text: "초안" },
                      { value: "ACTIVE", text: "확정" },
                  ];
        },
        draftStatSegmentOptions() {
            // UI에만 사용하는 간단 옵션 (백엔드 허용: DRAFT/ACTIVE/INACTIVE 등)
            const base = this.draftStatOptions;
            // DRAFT/ACTIVE만 보여주되, 기존 값이 다른 경우(예: INACTIVE)도 유지 가능하도록 마지막에 포함
            const exists = base.some((x) => x.value === this.editData.draftStat);
            return exists ? base : [...base, { value: this.editData.draftStat, text: this.editData.draftStat }];
        },
        modalTitle() {
            const titles = {
                create: "활동자료 등록",
                edit: "활동자료 편집",
                local: "데이터 편집",
            };
            return titles[this.mode] || "편집";
        },
        fixedPeriodFormatted() {
            if (!this.fixedPeriod) return "";
            const str = String(this.fixedPeriod).replace(/-/g, "");
            return str.length === 6 ? `${str.slice(0, 4)}-${str.slice(4, 6)}` : str;
        },
        isCreateMode() {
            return this.mode === "create";
        },
        // 기간 포맷 변환 (YYYYMM <-> YYYY-MM)
        periodFormatted: {
            get() {
                if (!this.editData.period) return "";
                const str = String(this.editData.period);
                if (str.length === 6) {
                    return `${str.slice(0, 4)}-${str.slice(4, 6)}`;
                }
                return str;
            },
            set(value) {
                if (!value) {
                    this.editData.period = "";
                    return;
                }
                // YYYY-MM -> YYYYMM
                this.editData.period = value.replace(/-/g, "");
            },
        },
        validatePeriod() {
            const period = this.fixedPeriod ? String(this.fixedPeriod).replace(/-/g, "") : this.editData.period;
            if (!period) return null;
            return /^\d{6}$/.test(period);
        },
        validateAmount() {
            if (this.editData.amount === null || this.editData.amount === undefined) return null;
            return this.editData.amount >= 0;
        },
        validateConfigIdx() {
            if (this.editData.configIdx === null || this.editData.configIdx === undefined) return null;
            return Number(this.editData.configIdx) > 0;
        },
        isValid() {
            return this.validatePeriod && this.validateAmount && this.validateConfigIdx && !!this.editData.draftStat;
        },
        hasChanges() {
            // create 모드: 유효성 검증만 체크 (새 데이터이므로 항상 "변경"으로 간주)
            if (this.mode === "create") {
                return this.isValid;
            }
            // local, edit 모드: 실제 변경사항 체크
            return this.changes.length > 0;
        },
        changes() {
            const changes = [];
            const fields = [
                { key: "period", label: "기간" },
                { key: "configIdx", label: "배출원(configIdx)" },
                { key: "amount", label: "활동량" },
                {
                    key: "draftStat",
                    label: "상태",
                    getDisplayValue: (v) => {
                        const opt = this.draftStatOptions.find((o) => o.value === v);
                        return opt ? opt.text : v || "-";
                    },
                },
                {
                    key: "dataSource",
                    label: "데이터 출처",
                    getDisplayValue: (v) => {
                        if (v == null || v === "") return "-";
                        const opt = this.dataSourceOptions.find((o) => o.value === v);
                        return opt ? opt.text : v;
                    },
                },
            ];

            fields.forEach((field) => {
                const oldValue = this.originalData[field.key];
                const newValue = this.editData[field.key];
                if (oldValue != newValue) {
                    const getDisplay = field.getDisplayValue || ((v) => v || "-");
                    changes.push({
                        field: field.key,
                        label: field.label,
                        oldValue: getDisplay(oldValue),
                        newValue: getDisplay(newValue),
                    });
                }
            });

            return changes;
        },
        selectedConfig() {
            const idx = Number(this.editData.configIdx);
            if (!idx) return null;
            return this.configs.find((c) => Number(c.configIdx) === idx) || null;
        },
        filteredConfigs() {
            const q = String(this.configSearch || "")
                .trim()
                .toLowerCase();
            const list = Array.isArray(this.configs) ? this.configs : [];
            if (!q) return list;
            const pick = (cfg) => {
                const facilityName = cfg?.facility?.facilityName || "";
                const facilityCode = cfg?.facility?.facilityCode || "";
                const categoryName = cfg?.category?.categoryName || "";
                const categoryCode = cfg?.category?.categoryCode || "";
                const tier = cfg?.tier || "";
                return `${facilityName} ${facilityCode} ${categoryName} ${categoryCode} ${tier} ${cfg.configIdx}`.toLowerCase();
            };
            return list.filter((cfg) => pick(cfg).includes(q));
        },
    },
    watch: {
        visible(newVal) {
            this.isVisible = newVal;
            if (newVal) {
                this.initializeModal();
            }
        },
    },
    methods: {
        onModalShown() {
            this.initializeModal();
            this.loadConfigs();
        },

        initializeModal() {
            if (this.mode === "create") {
                const periodVal = this.fixedPeriod ? String(this.fixedPeriod).replace(/-/g, "") : "";
                // 생성 모드: 빈 데이터 (fixedPeriod 있으면 기간 고정)
                this.editData = {
                    activityIdx: null,
                    userIdx: this.loginUser?.userIdx || null,
                    period: periodVal,
                    amount: 0,
                    configIdx: null,
                    draftStat: "DRAFT",
                    dataSource: null,
                };
            } else {
                // 편집 모드: 원본 데이터 복사 (fixedPeriod 있으면 기간 고정)
                const periodVal = this.fixedPeriod
                    ? String(this.fixedPeriod).replace(/-/g, "")
                    : this.rowData.period || "";
                this.editData = {
                    activityIdx: this.rowData.activityIdx || null,
                    userIdx: this.rowData.userIdx || null,
                    period: periodVal,
                    amount: this.rowData.amount || 0,
                    configIdx: this.rowData.configIdx || null,
                    draftStat: this.rowData.draftStat || "DRAFT",
                    dataSource: this.rowData.dataSource != null ? this.rowData.dataSource : null,
                };
            }

            // 원본 데이터 저장 (변경 사항 비교용)
            this.originalData = { ...this.editData };
        },

        async loadConfigs() {
            // activity-configs 조회는 orgIdx 기준이 가장 확실 (ActivityDataViewer/Workflow에서 고정 orgIdx 전달)
            const orgIdx = this.fixedOrgIdx != null && this.fixedOrgIdx !== "" ? Number(this.fixedOrgIdx) : null;
            if (!orgIdx) {
                this.configs = [];
                return;
            }

            this.loadingConfigs = true;
            try {
                const month = String(this.fixedPeriod || this.editData.period || "").replace(/-/g, "");
                const res = await carbonApi.activityConfig.getConfigs({
                    orgIdx,
                    draftStat: "ACTIVE",
                    month: /^\d{6}$/.test(month) ? month : undefined,
                });
                const data = res?.data?.data || res?.data;
                const list = Array.isArray(data) ? data : data?.configs || [];
                this.configs = Array.isArray(list) ? list : [];

                const codes = Array.from(
                    new Set(this.configs.map((c) => c?.category?.categoryCode).filter((x) => x != null && x !== "")),
                );
                await this.loadCategoryDetails(codes);

                // create 모드에서 아직 선택이 없으면, displayOrder 기준으로 1건 자동 선택
                if (!this.editData.configIdx && this.configs.length > 0) {
                    const sorted = [...this.configs].sort((a, b) => {
                        const aOrder = Number(a.displayOrder ?? 999999);
                        const bOrder = Number(b.displayOrder ?? 999999);
                        if (aOrder !== bOrder) return aOrder - bOrder;
                        return Number(a.configIdx) - Number(b.configIdx);
                    });
                    const first = sorted[0];
                    this.editData.configIdx = first?.configIdx ?? null;
                    if (
                        this.mode === "create" &&
                        first &&
                        (this.editData.dataSource == null || this.editData.dataSource === "")
                    ) {
                        this.editData.dataSource = first.dataSource != null ? first.dataSource : null;
                    }
                }

                this.$nextTick(() => {
                    const el = this.$refs.sidebarList;
                    if (!el) {
                        this.isSidebarScrolledToEnd = true;
                        this.isSidebarScrolledToTop = true;
                        return;
                    }
                    this.isSidebarScrolledToEnd = el.scrollHeight <= el.clientHeight + 2;
                    this.isSidebarScrolledToTop = el.scrollTop <= 2;
                });
            } catch (e) {
                console.error("[EditRowModal] activity-configs 로드 실패:", e);
                this.configs = [];
            } finally {
                this.loadingConfigs = false;
            }
        },

        async loadCategoryDetails(categoryCodes = []) {
            const codes = Array.isArray(categoryCodes) ? categoryCodes : [];
            const missing = codes.filter((code) => !this.categoryDetailByCode[code]);
            if (missing.length === 0) return;

            const results = await Promise.all(
                missing.map(async (code) => {
                    try {
                        const res = await carbonApi.category.getCategoryByCode(code);
                        const data = res?.data?.data || res?.data;
                        return { code, data: data || null };
                    } catch (e) {
                        return { code, data: null };
                    }
                }),
            );

            const next = { ...this.categoryDetailByCode };
            results.forEach((r) => {
                next[r.code] = r.data;
            });
            this.categoryDetailByCode = next;
        },

        getScopeLabel(cfg) {
            const code = cfg?.category?.categoryCode;
            if (!code) return null;
            const detail = this.categoryDetailByCode[code];
            const raw = detail?.scopeType;
            if (!raw) return null;
            const norm = String(raw).toUpperCase();
            if (norm.includes("SCOPE1")) return "Scope 1";
            if (norm.includes("SCOPE2")) return "Scope 2";
            if (norm.includes("SCOPE3")) return "Scope 3";
            return null;
        },

        onSidebarScroll(event) {
            const el = event?.target;
            if (!el) return;
            const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
            const atTop = el.scrollTop <= 2;
            this.isSidebarScrolledToEnd = nearBottom;
            this.isSidebarScrolledToTop = atTop;
        },

        selectConfig(cfg) {
            if (!cfg) return;
            this.editData.configIdx = cfg.configIdx;
            if (this.mode === "create" && (this.editData.dataSource == null || this.editData.dataSource === "")) {
                this.editData.dataSource = cfg.dataSource != null ? cfg.dataSource : null;
            }
        },

        async saveChanges() {
            if (!this.isValid || !this.hasChanges) return;

            this.saving = true;

            try {
                // mode에 따라 다른 데이터 구성
                const saveData = {
                    mode: this.mode,
                    activityIdx: this.editData.activityIdx || undefined,
                    configIdx: this.editData.configIdx,
                    period: this.editData.period,
                    amount: this.editData.amount,
                    draftStat: this.editData.draftStat,
                    dataSource: this.editData.dataSource === "" ? null : (this.editData.dataSource ?? null),
                };

                // edit 모드: activityIdx 포함
                if (this.mode === "edit" && this.editData.activityIdx) {
                    saveData.activityIdx = this.editData.activityIdx;
                }

                // create 모드: userIdx 포함
                if (this.mode === "create" && this.loginUser) {
                    saveData.userIdx = this.loginUser.userIdx;
                }

                // local 모드 (ActivityUploader): rowNumber 포함
                if (this.mode === "local" && this.rowData.rowNumber) {
                    saveData.rowNumber = this.rowData.rowNumber;
                }

                if (this.onSave) {
                    // onSave가 있으면 await 후 부모가 성공 시 모달 닫기 담당
                    await this.onSave(saveData);
                    this.originalData = { ...this.editData };
                } else {
                    // 하위 호환: @save 이벤트만 사용하는 경우
                    this.$emit("save", saveData);
                    this.originalData = { ...this.editData };
                    this.$bvToast.toast(
                        this.mode === "create" ? "활동자료가 등록되었습니다." : "데이터가 수정되었습니다.",
                        { title: "저장 완료", variant: "success", solid: true, toaster: "b-toaster-bottom-right" },
                    );
                    setTimeout(() => this.$emit("close"), 500);
                }
            } catch (error) {
                console.error("[EditRowModal] 저장 실패:", error);
                // onSave 사용 시 부모(ActivityDataViewer 등)가 이미 토스트 표시
                if (!this.onSave) {
                    this.$bvToast.toast(error?.message || "저장 중 오류가 발생했습니다.", {
                        title: "저장 실패",
                        variant: "danger",
                        solid: true,
                        toaster: "b-toaster-bottom-right",
                    });
                }
            } finally {
                this.saving = false;
            }
        },

        handleHide(bvModalEvent) {
            // 변경사항이 있고 저장 중이 아닐 때만 확인
            if (this.hasChanges && !this.saving) {
                bvModalEvent.preventDefault();
                this.$bvModal
                    .msgBoxConfirm("변경 사항이 저장되지 않습니다. 닫으시겠습니까?", {
                        title: "확인",
                        size: "sm",
                        buttonSize: "sm",
                        okVariant: "danger",
                        okTitle: "닫기",
                        cancelTitle: "취소",
                        centered: true,
                    })
                    .then((value) => {
                        if (value) {
                            this.saving = true;
                            this.$bvModal.hide("edit-row-modal");
                            this.$emit("close");
                            this.$nextTick(() => {
                                this.saving = false;
                            });
                        }
                    });
            } else {
                this.$emit("close");
            }
        },
    },
};
</script>

<style scoped>
/* b-modal 기본 프레임 제거 (헤더/바깥 테두리/그림자) */
::v-deep .carbon-frameless-modal-content {
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
}

::v-deep .carbon-frameless-modal-content .modal-body {
    padding: 0 !important;
    background: transparent !important;
}

.edit-row-container {
    padding: 0;
}

.period-wrap {
    width: 100%;
    max-width: 100%;
}

.period-picker {
    width: 100%;
}

/* Split View (provided design adapted to existing Bootstrap-Vue stack) */
.split-root {
    display: flex;
    height: 720px;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    overflow: hidden;
    background: #ffffff;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.split-sidebar {
    width: 320px;
    border-right: 1px solid #f1f5f9;
    background: rgba(248, 250, 252, 0.65);
    display: flex;
    flex-direction: column;
}

.sidebar-head {
    padding: 18px;
    border-bottom: 1px solid #f1f5f9;
    background: #fff;
}

.sidebar-title {
    font-weight: 800;
    font-size: 15px;
    letter-spacing: 0.12em;
    color: #0f172a;
}

.sidebar-sub {
    margin-top: 6px;
    font-size: 12px;
    color: #64748b;
}

.search-wrap {
    position: relative;
    margin-top: 10px;
}

.search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 12px;
}

.search-input {
    width: 100%;
    height: 34px;
    padding: 0 10px 0 30px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #fff;
    font-size: 12px;
    outline: none;
}

.sidebar-meta {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.badge-soft {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
    padding: 3px 8px;
    border-radius: 999px;
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
}

.meta-text {
    font-size: 12px;
    color: #64748b;
}

.sidebar-list-wrap {
    flex: 1;
    min-height: 0;
    position: relative;
    display: flex;
    flex-direction: column;
}

.sidebar-list {
    flex: 1;
    min-height: 0;
    padding: 12px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sidebar-list-blur {
    position: absolute;
    left: 0;
    right: 7px;
    height: 25px;
    z-index: 1;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    pointer-events: none;
}

.sidebar-list-blur-top,
.sidebar-list-blur-bottom {
    background: linear-gradient(to top, rgba(248, 250, 252, 0.82), transparent);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
}

.sidebar-list-blur-bottom {
    bottom: 0;
}

.sidebar-list-blur-top {
    top: 0;
    transform: rotate(180deg);
}

.cfg-item {
    width: 100%;
    text-align: left;
    border: 1px solid #e2e8f0;
    background: rgba(255, 255, 255, 0.7);
    padding: 12px;
    border-radius: 14px;
    cursor: pointer;
    transition: all 140ms ease;
    position: relative;
}

.cfg-item:hover {
    background: #fff;
    border-color: #cbd5e1;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
}

.cfg-item.active {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(236, 253, 245, 0.55));
    box-shadow:
        0 18px 38px rgba(16, 185, 129, 0.14),
        0 8px 18px rgba(15, 23, 42, 0.08);
    transform: translateY(-1px);
}

/* subtle depth for non-hover cards too */
.cfg-item:not(.active) {
    box-shadow: 0 1px 0 rgba(15, 23, 42, 0.02);
}

.cfg-item:disabled,
.cfg-item.disabled {
    cursor: not-allowed;
    opacity: 0.7;
    pointer-events: none;
}

.cfg-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.cfg-top-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.scope-pill {
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.08em;
    padding: 3px 8px;
    border-radius: 999px;
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
}

.cfg-facility {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: #059669;
}

.cfg-name {
    margin-top: 6px;
    font-size: 14px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.25;
}

.cfg-sub {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.cfg-chip {
    font-size: 10px;
    font-weight: 700;
    color: #64748b;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    padding: 2px 8px;
    border-radius: 999px;
}

.sidebar-empty {
    padding: 18px;
    color: #64748b;
    font-size: 12px;
}

.empty-title {
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 4px;
}

.empty-desc {
    color: #64748b;
}

.split-detail {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #fff;
    overflow: hidden;
}

.detail-body {
    flex: 1;
    overflow: auto;
    padding: 22px;
}

.detail-head {
    margin-bottom: 14px;
}

.detail-title {
    font-size: 18px;
    font-weight: 800;
    color: #0f172a;
}

.detail-sub {
    margin-top: 6px;
    font-size: 12px;
    color: #64748b;
}

.detail-card {
    border: 1px solid #f1f5f9;
    border-radius: 16px;
    padding: 16px;
    background: #ffffff;
}

.config-summary-bar {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    padding: 14px 14px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 16px;
    margin-bottom: 14px;
}

.cs-item {
    min-width: 0;
}

.cs-k {
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.08em;
    color: #94a3b8;
}

.cs-v {
    margin-top: 6px;
    font-size: 12px;
    font-weight: 800;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.summary-row {
    display: flex;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid #f1f5f9;
    margin-bottom: 14px;
}

.summary-k {
    width: 64px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    color: #94a3b8;
}

.summary-v {
    flex: 1;
}

.summary-v.muted {
    color: #94a3b8;
    font-size: 12px;
}

.summary-main {
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
}

.summary-sub {
    margin-top: 4px;
    font-size: 12px;
    color: #64748b;
}

/* 1행: 기간 | 활동량, 2행: 상태 | 데이터 출처 */
.form-fields {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.form-fields__row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 12px;
    align-items: start;
}

.form-fields__row .form-block {
    min-width: 0;
}

.form-fields__row--top {
    padding-bottom: 14px;
}

/* CarbonSelect — 활동량 입력과 높이·테두리 톤 맞춤 */
::v-deep select.entry-row-select {
    width: 100%;
    min-height: 40px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    font-size: 12px;
    padding: 8px 10px;
}

.form-label {
    display: block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: #64748b;
    margin-bottom: 6px;
}

.form-input {
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    min-height: 40px;
}
.form-input:focus,
.form-input:active {
    border-color: #e2e8f0;
    box-shadow: none;
    outline: none;
}

.input-with-suffix {
    position: relative;
}

.suffix {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 12px;
    border-left: 1px solid #f1f5f9;
    background: #f8fafc;
    color: #64748b;
    font-size: 11px;
    font-weight: 800;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    width: 100px;
    text-align: center;
    justify-content: center;
    align-items: center;
}

.segmented {
    display: flex;
    padding: 4px;
    background: #f1f5f9;
    border-radius: 12px;
    gap: 4px;
}

.segmented--compact {
    padding: 3px;
    gap: 3px;
    min-height: 40px;
    align-items: center;
}

.seg-btn {
    flex: 1;
    border: none;
    background: transparent;
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 800;
    color: #64748b;
    cursor: pointer;
    transition: all 200ms ease;
}

.segmented--compact .seg-btn {
    padding: 6px 8px;
    font-size: 11px;
    border-radius: 9px;
}

.seg-btn.active {
    background: #fff;
    color: #0f172a;
    box-shadow:
        0 1px 0 rgba(15, 23, 42, 0.05),
        0 6px 18px rgba(15, 23, 42, 0.06);
}

.detail-footer {
    padding: 14px 18px;
    border-top: 1px solid #f1f5f9;
    background: rgba(248, 250, 252, 0.45);
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

@media (max-width: 992px) {
    .split-root {
        height: auto;
        flex-direction: column;
    }
    .split-sidebar {
        width: 100%;
        max-height: 260px;
        border-right: none;
        border-bottom: 1px solid #f1f5f9;
    }
    .form-fields__row {
        grid-template-columns: 1fr;
        row-gap: 14px;
    }

    .config-summary-bar {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
